import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getPageSeo,
  ORGANIZATION_ADDRESS,
  ORGANIZATION_CONTACT,
  ORGANIZATION_NAME,
  SITE_URL,
  SOCIAL_PROFILE_URLS,
} from "../seo/seoConfig";

const setMetaTag = (attributeName, attributeValue, content) => {
  if (!content) {
    return;
  }

  const selector = `meta[${attributeName}="${attributeValue}"]`;
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setCanonicalLink = (href) => {
  let canonicalTag = document.head.querySelector('link[rel="canonical"]');

  if (!canonicalTag) {
    canonicalTag = document.createElement("link");
    canonicalTag.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalTag);
  }

  canonicalTag.setAttribute("href", href);
};

const setAlternateLanguageLink = (hreflang, href) => {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
  let alternateTag = document.head.querySelector(selector);

  if (!alternateTag) {
    alternateTag = document.createElement("link");
    alternateTag.setAttribute("rel", "alternate");
    alternateTag.setAttribute("hreflang", hreflang);
    document.head.appendChild(alternateTag);
  }

  alternateTag.setAttribute("href", href);
};

const setJsonLdScript = (identifier, payload) => {
  const selector = `script[data-seo-id="${identifier}"]`;
  let scriptTag = document.head.querySelector(selector);

  if (!scriptTag) {
    scriptTag = document.createElement("script");
    scriptTag.setAttribute("type", "application/ld+json");
    scriptTag.setAttribute("data-seo-id", identifier);
    document.head.appendChild(scriptTag);
  }

  scriptTag.textContent = JSON.stringify(payload);
};

const buildSchemaGraph = (pageSeo) => {
  const websiteId = `${SITE_URL}/#website`;
  const organizationId = `${SITE_URL}/#organization`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": organizationId,
        name: ORGANIZATION_NAME,
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/vessella-logo.png`,
        },
        address: {
          "@type": "PostalAddress",
          ...ORGANIZATION_ADDRESS,
        },
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "customer support",
            telephone: ORGANIZATION_CONTACT.telephone,
            email: ORGANIZATION_CONTACT.email,
            areaServed: "IN",
            availableLanguage: ["en", "te", "hi"],
          },
        ],
        sameAs: SOCIAL_PROFILE_URLS,
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        name: ORGANIZATION_NAME,
        url: `${SITE_URL}/`,
        publisher: {
          "@id": organizationId,
        },
        inLanguage: "en-IN",
      },
      {
        "@type": "WebPage",
        "@id": `${pageSeo.url}#webpage`,
        name: pageSeo.title,
        description: pageSeo.description,
        url: pageSeo.url,
        inLanguage: "en-IN",
        isPartOf: {
          "@id": websiteId,
        },
        about: {
          "@id": organizationId,
        },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: pageSeo.imageUrl,
        },
      },
    ],
  };
};

const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageSeo = getPageSeo(pathname);
    const keywordString = pageSeo.keywords.join(", ");

    document.title = pageSeo.title;

    setCanonicalLink(pageSeo.url);
    setAlternateLanguageLink("en-IN", pageSeo.url);
    setAlternateLanguageLink("x-default", pageSeo.url);

    setMetaTag("name", "description", pageSeo.description);
    setMetaTag("name", "keywords", keywordString);
    setMetaTag("name", "robots", pageSeo.robots);
    setMetaTag("name", "theme-color", "#0e2f53");

    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:site_name", ORGANIZATION_NAME);
    setMetaTag("property", "og:locale", "en_IN");
    setMetaTag("property", "og:title", pageSeo.title);
    setMetaTag("property", "og:description", pageSeo.description);
    setMetaTag("property", "og:url", pageSeo.url);
    setMetaTag("property", "og:image", pageSeo.imageUrl);
    setMetaTag("property", "og:image:alt", `${ORGANIZATION_NAME} logo`);

    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", pageSeo.title);
    setMetaTag("name", "twitter:description", pageSeo.description);
    setMetaTag("name", "twitter:image", pageSeo.imageUrl);

    setJsonLdScript("website-graph", buildSchemaGraph(pageSeo));
  }, [pathname]);

  return null;
};

export default SeoManager;
